import type {
    AdminConfigRow,
    Client,
    ClientConfigRow,
    ConfigDefinition,
    Domain,
} from '@inboundx/shared/config';
import type { Json } from '@inboundx/shared/types';
import {
    isSelfServePlan,
    type SelfServePlan,
    type SelfServePlanAssignment,
} from '@/domain/selfServePlans';
import { supabase } from '@/lib/supabase';

/** Fetch domain row from public.domains. */
export async function fetchDomain(rawDomain: string): Promise<Domain | null> {
    const { data, error } = await supabase
        .from('domains')
        .select('domain, client_id')
        .eq('domain', rawDomain)
        .single();

    if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
    }
    return data;
}

/** Fetch client row from public.clients. */
export async function fetchClient(clientId: string): Promise<Client | null> {
    const { data, error } = await supabase
        .from('clients')
        .select('id, name, enabled')
        .eq('id', clientId)
        .single();

    if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
    }
    return data;
}

/** Fetch all config definitions from config.configs. */
export async function fetchConfigDefinitions(): Promise<ConfigDefinition[]> {
    const { data, error } = await supabase
        .schema('config')
        .from('configs')
        .select(
            'slug, scope, name, description, category, is_feature, enabled_by_default, requires, default_config, merge_strategy'
        );

    if (error) throw error;
    return (data ?? []) as ConfigDefinition[];
}

/** Fetch per-domain client config overrides from config.client_configs. */
export async function fetchClientConfigs(rawDomain: string): Promise<ClientConfigRow[]> {
    const { data, error } = await supabase
        .schema('config')
        .from('client_configs')
        .select('domain, config_slug, config_scope, enabled, config')
        .eq('domain', rawDomain);

    if (error) throw error;
    return (data ?? []) as ClientConfigRow[];
}

/** Fetch the authoritative plan entitlement and its protected quota-cycle anchor. */
export async function fetchSelfServePlan(
    rawDomain: string
): Promise<SelfServePlanAssignment | null> {
    // This table is introduced by IX-5038 and is not available in the checked-in
    // generated database types until the migration is applied to a generation target.
    const { data, error } = await (
        supabase.schema('config') as unknown as {
            from: (table: 'self_serve_plan_entitlements') => {
                select: (columns: string) => {
                    eq: (
                        column: string,
                        value: string
                    ) => {
                        maybeSingle: () => Promise<{
                            data: {
                                plan: unknown;
                                monthly_conversation_limit: unknown;
                                quota_started_at: unknown;
                            } | null;
                            error: { message: string } | null;
                        }>;
                    };
                };
            };
        }
    )
        .from('self_serve_plan_entitlements')
        .select('plan, monthly_conversation_limit, quota_started_at')
        .eq('domain', rawDomain)
        .maybeSingle();

    if (error) throw error;
    const limit = data?.monthly_conversation_limit;
    if (!isSelfServePlan(data?.plan) || (typeof limit !== 'number' && limit !== null)) {
        return null;
    }

    return {
        plan: data.plan,
        monthlyConversationLimit: limit,
        quotaStartedAt: typeof data.quota_started_at === 'string' ? data.quota_started_at : null,
    };
}

/** Fetch all staff-global admin config rows from config.admin_configs. */
export async function fetchAdminConfigs(): Promise<AdminConfigRow[]> {
    // admin_configs is not yet in the generated Supabase types (user-owned migration).
    // Cast is safe: schema enforces (config_slug, 'admin', enabled, config) columns.
    const { data, error } = await (
        supabase.schema('config') as unknown as {
            from: (table: string) => {
                select: (columns: string) => Promise<{
                    data: AdminConfigRow[] | null;
                    error: { message: string } | null;
                }>;
            };
        }
    )
        .from('admin_configs')
        .select('config_slug, config_scope, enabled, config');

    if (error) throw error;
    return data ?? [];
}

/** Upsert an admin (staff-global) config override for a slug. */
export async function upsertAdminConfig(
    configSlug: string,
    payload: { enabled?: boolean | null; config?: Record<string, unknown> | null }
): Promise<void> {
    const row = {
        config_slug: configSlug,
        config_scope: 'admin',
        enabled: payload.enabled ?? null,
        config: (payload.config ?? null) as Json,
    };
    const { error } = await (
        supabase.schema('config') as unknown as {
            from: (table: string) => {
                upsert: (
                    row: Record<string, unknown>,
                    options: { onConflict: string }
                ) => Promise<{ error: { message: string } | null }>;
            };
        }
    )
        .from('admin_configs')
        .upsert(row, { onConflict: 'config_slug,config_scope' });
    if (error) throw error;
}

/** Upsert a client config override for a domain + slug. */
export async function upsertClientConfig(
    domain: string,
    configSlug: string,
    configScope: string,
    payload: { enabled?: boolean | null; config?: Record<string, unknown> | null }
): Promise<void> {
    const { error } = await supabase
        .schema('config')
        .from('client_configs')
        .upsert(
            {
                domain,
                config_slug: configSlug,
                config_scope: configScope,
                enabled: payload.enabled ?? null,
                config: (payload.config ?? null) as Json,
            },
            { onConflict: 'domain,config_slug,config_scope' }
        );
    if (error) throw error;
}

/** Atomically merge a patch into the domain's onboarding config. */
export async function mergeOnboardingState(domain: string, patch: Json): Promise<void> {
    const { error } = await supabase.schema('config').rpc('merge_onboarding_state', {
        p_domain: domain,
        p_patch: patch,
    });

    if (error) throw error;
}

/** Persist a self-serve selection and activate its supported entitlement tier. */
export async function selectSelfServePlan(domain: string, plan: SelfServePlan): Promise<void> {
    const { error } = await supabase.schema('config').rpc('select_self_serve_plan', {
        p_domain: domain,
        p_plan: plan,
    });

    if (error) throw error;
}

/** Correct a self-serve quota-cycle anchor through the staff-only entitlement RPC. */
export async function setSelfServeQuotaStartedAt(
    domain: string,
    quotaStartedAt: string
): Promise<void> {
    const { error } = await (
        supabase.schema('config') as unknown as {
            rpc: (
                functionName: 'set_self_serve_quota_started_at',
                params: { p_domain: string; p_quota_started_at: string }
            ) => Promise<{ error: { message: string } | null }>;
        }
    ).rpc('set_self_serve_quota_started_at', {
        p_domain: domain,
        p_quota_started_at: quotaStartedAt,
    });

    if (error) throw error;
}
